import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Card from '../templates/ui/Card'
import Button from '../templates/ui/Button'
import CenterPopup from '../templates/ui/CenterPopup'
import CenterToast from '../templates/ui/CenterToast'
import UserAvatar from '../templates/ui/UserAvatar'
import { buildApiErrorPopupTemplate } from '../templates/popupTemplate'
import { useCampaignStore } from '../app/store/campaignStore'
import { muteluApi } from '../services/muteluApi'
import { logPageApiCall, logPageApiError, logPageApiResult } from '../utils/apiDebugLogger'
import './Activity.css'

const FORCE_TICKET_REFRESH_KEY = 'mutelu:force-ticket-refresh'
const TICKET_BALANCE_SNAPSHOT_KEY = 'mutelu:ticket-balance:snapshot'
const FORCE_PARTY_REFRESH_KEY = 'mutelu:force-party-refresh'
const PARTY_OWNED_SNAPSHOT_KEY = 'mutelu:party-owned:snapshot'
const PARTY_JOINED_SNAPSHOT_KEY = 'mutelu:party-joined:snapshot'
const DEFAULT_TICKET_BALANCE = {
  total: 0,
  gold: 0,
  silver: 0,
  bronze: 0
}
const getGroupCode = (payload) =>
  payload?.group_code ||
  payload?.group?.group_code ||
  payload?.data?.group_code ||
  payload?.data?.group?.group_code ||
  null
const getGroupStatus = (payload) =>
  String(
    payload?.status ||
      payload?.group?.status ||
      payload?.data?.status ||
      payload?.data?.group?.status ||
      ''
  )
    .trim()
    .toLowerCase()
const getPartyRoleLabel = (isOwner, status) => {
  if (!isOwner) return 'ลูกตี้'
  const normalizedStatus = String(status || '').trim().toLowerCase()
  if (normalizedStatus === 'completed' || normalizedStatus === 'locked') {
    return 'ปาร์ตี้สำเร็จ'
  }
  return 'หัวหน้าตี้'
}
const normalizeMessage = (value, fallback = '') => {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message
    if (typeof value.data?.message === 'string') return value.data.message
    try {
      return JSON.stringify(value)
    } catch {
      return fallback
    }
  }
  return String(value)
}
const CONNECTION_ERROR_MESSAGE = 'ไม่สามารถเชื่อมต่อกับระบบ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต'
const isConnectionError = (message) => /ไม่สามารถเชื่อมต่อ|ตรวจสอบอินเทอร์เน็ต|network|timeout/i.test(String(message || ''))
const toSafeNumber = (value) => {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 ? n : 0
}
const normalizeTicketBalance = (payload) => {
  const source = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {}
  const gold = toSafeNumber(source.gold)
  const silver = toSafeNumber(source.silver)
  const bronze = toSafeNumber(source.bronze)
  const total = toSafeNumber(source.total) || (gold + silver + bronze)
  return { total, gold, silver, bronze }
}
const normalizeMeritRoomMap = (payload) => {
  const list =
    Array.isArray(payload?.data) ? payload.data :
      Array.isArray(payload) ? payload : []
  return list.reduce((acc, item) => {
    const code = item?.room_id || item?.group_code
    if (!code) return acc
    acc[code] = item
    return acc
  }, {})
}
const countUniqueMembers = (members) => {
  const list = Array.isArray(members) ? members : []
  if (list.length === 0) return 0
  const seen = new Set()
  list.forEach((member, index) => {
    const key =
      member?.player_id ||
      member?.line_mid ||
      member?.line_id ||
      member?.membership_id ||
      (typeof member?.display_name === 'string' ? member.display_name.trim() : '') ||
      `member-${index + 1}`
    if (!key) return
    seen.add(String(key))
  })
  return seen.size
}

const buildPartyRowFromSnapshot = (group, ownedCode, localPicture) => {
  const code = getGroupCode(group)
  if (!code) return null
  const isOwner = Boolean(group?.is_owner || code === ownedCode)
  const fallbackMembersCount =
    countUniqueMembers(group?.members) ||
    toSafeNumber(group?.members_count) ||
    1
  const status = getGroupStatus(group)
  return {
    id: code,
    name: group?.group_name || `ตี้ ${code}`,
    avatar: group?.owner?.picture_url || localPicture || '🙂',
    role: getPartyRoleLabel(isOwner, status),
    members: `สมาชิก ${fallbackMembersCount} / 4 คน`,
    status,
    to: `/game/mutelu/party/${code}`
  }
}

export default function Activity() {
  const location = useLocation()
  const { state } = useCampaignStore()
  const isMemberMode = location.pathname.includes('/game/mutelu/activity-member')
  const localPicture = useMemo(() => localStorage.getItem('line_picture_url') || '', [])
  const [partyApiRows, setPartyApiRows] = useState([])
  const [hasOwnedParty, setHasOwnedParty] = useState(false)
  const [meritRoomMap, setMeritRoomMap] = useState({})
  const [ticketBalance, setTicketBalance] = useState(DEFAULT_TICKET_BALANCE)
  const [apiPopupTemplate, setApiPopupTemplate] = useState(null)
  const [centerToast, setCenterToast] = useState('')
  const centerToastTimerRef = useRef(null)
  const goldTicket = ticketBalance.gold
  const silverTicket = ticketBalance.silver
  const bronzeTicket = ticketBalance.bronze
  const ticketBuckets = [
    { tone: 'gold', label: 'ตั๋วทอง', count: goldTicket },
    { tone: 'silver', label: 'ตั๋วเงิน', count: silverTicket },
    { tone: 'bronze', label: 'ตั๋วทองแดง', count: bronzeTicket }
  ]

  const partyRows = partyApiRows
  const displayPartyRows = useMemo(
    () => partyRows.slice(0, isMemberMode ? 1 : 3),
    [isMemberMode, partyRows]
  )
  const showPartyAndMeritSteps =
    isMemberMode ||
    hasOwnedParty ||
    partyApiRows.length > 0 ||
    Object.keys(meritRoomMap || {}).length > 0
  const showStep4 = false

  const partyRowMap = useMemo(
    () =>
      new Map(
        partyRows.map((party) => [
          party.id,
          party
        ])
      ),
    [partyRows]
  )
  const meritApiRooms = useMemo(() => {
    const rooms = Object.values(meritRoomMap || {})
      .map((meritRoom) => {
        const roomId = meritRoom?.room_id || meritRoom?.group_code
        if (!roomId) return null
        const party = partyRowMap.get(roomId)
        return {
          id: roomId,
          name: meritRoom.room_name || party?.name || `Party ${String(roomId).slice(0, 6).toUpperCase()}`,
          ownerAvatar: party?.avatar || localPicture || '🙂',
          bathDoneToday: Boolean(meritRoom.bath_done_today),
          membersCount: toSafeNumber(meritRoom.members_count) || 4,
          filledSegments: toSafeNumber(meritRoom.filled_segments),
          passToday: Boolean(meritRoom.pass_today)
        }
      })
      .filter(Boolean)

    const partyOrderMap = new Map(
      partyRows.map((party, index) => [String(party?.id || '').trim().toUpperCase(), index])
    )
    rooms.sort((a, b) => {
      const codeA = String(a?.id || '').trim().toUpperCase()
      const codeB = String(b?.id || '').trim().toUpperCase()
      const orderA = partyOrderMap.has(codeA) ? partyOrderMap.get(codeA) : Number.MAX_SAFE_INTEGER
      const orderB = partyOrderMap.has(codeB) ? partyOrderMap.get(codeB) : Number.MAX_SAFE_INTEGER
      if (orderA !== orderB) return orderA - orderB
      return codeB.localeCompare(codeA)
    })

    return rooms
  }, [localPicture, meritRoomMap, partyRowMap, partyRows])
  const bathRooms = meritApiRooms

  useEffect(() => {
    const rawSnapshot = localStorage.getItem(TICKET_BALANCE_SNAPSHOT_KEY)
    if (!rawSnapshot) return
    try {
      const parsedSnapshot = JSON.parse(rawSnapshot)
      const normalizedSnapshot = normalizeTicketBalance(parsedSnapshot)
      setTicketBalance(normalizedSnapshot)
      logPageApiResult('Activity', 'ticket balance snapshot', normalizedSnapshot)
    } catch (error) {
      logPageApiError('Activity', 'ticket balance snapshot parse', error)
    }
  }, [location.key])

  useEffect(() => {
    const ownedRaw = localStorage.getItem(PARTY_OWNED_SNAPSHOT_KEY)
    const joinedRaw = localStorage.getItem(PARTY_JOINED_SNAPSHOT_KEY)
    const snapshotRows = []
    let ownedCode = null

    try {
      const ownedSnapshot = ownedRaw ? JSON.parse(ownedRaw) : null
      ownedCode = getGroupCode(ownedSnapshot)
      const ownedRow = buildPartyRowFromSnapshot(ownedSnapshot, ownedCode, localPicture)
      if (ownedRow && getGroupStatus(ownedSnapshot) !== 'expired') {
        snapshotRows.push(ownedRow)
        setHasOwnedParty(true)
      }
    } catch (error) {
      logPageApiError('Activity', 'party owned snapshot parse', error)
    }

    try {
      const joinedSnapshot = joinedRaw ? JSON.parse(joinedRaw) : null
      const joinedRow = buildPartyRowFromSnapshot(joinedSnapshot, ownedCode, localPicture)
      if (joinedRow && getGroupStatus(joinedSnapshot) !== 'expired') {
        const duplicate = snapshotRows.some((row) => row.id === joinedRow.id)
        if (!duplicate) {
          snapshotRows.push(joinedRow)
        }
      }
    } catch (error) {
      logPageApiError('Activity', 'party joined snapshot parse', error)
    }

    if (snapshotRows.length > 0) {
      setPartyApiRows(snapshotRows)
      logPageApiResult('Activity', 'party snapshot rows', snapshotRows)
    }
  }, [isMemberMode, localPicture, location.key])

  useEffect(() => {
    let mounted = true

    const fetchParties = async () => {
      try {
        logPageApiCall('Activity', 'bootstrap', { isMemberMode })
        const shouldForceTicketRefresh = Boolean(localStorage.getItem(FORCE_TICKET_REFRESH_KEY))
        const shouldForcePartyRefresh = Boolean(localStorage.getItem(FORCE_PARTY_REFRESH_KEY))
        muteluApi.initPlayerOnce().then(() => {
          logPageApiResult('Activity', 'initPlayer', { success: true })
        }).catch((error) => {
          logPageApiError('Activity', 'initPlayer', error)
        })

        logPageApiCall('Activity', 'Promise.allSettled party/ticket/merit')
        const [ticketResult, meritResult, ownedResult, joinedResult] = await Promise.allSettled([
          muteluApi.getTicketBalance(shouldForceTicketRefresh),
          muteluApi.getMeritRooms(shouldForcePartyRefresh),
          muteluApi.getMyOwnedParty(shouldForcePartyRefresh),
          muteluApi.getMyJoinedParties(shouldForcePartyRefresh)
        ])
        logPageApiResult('Activity', 'Promise.allSettled party/ticket/merit', {
          ticket: ticketResult.status,
          merit: meritResult.status,
          owned: ownedResult.status,
          joined: joinedResult.status
        })
        if (!mounted) return

        if (ticketResult.status === 'fulfilled') {
          const normalizedTicket = normalizeTicketBalance(ticketResult.value)
          logPageApiResult('Activity', 'getTicketBalance normalized', {
            raw: ticketResult.value,
            normalized: normalizedTicket
          })
          setTicketBalance(normalizedTicket)
          if (shouldForceTicketRefresh) {
            localStorage.removeItem(FORCE_TICKET_REFRESH_KEY)
          }
          localStorage.setItem(TICKET_BALANCE_SNAPSHOT_KEY, JSON.stringify(normalizedTicket))
        } else {
          const ticketMessage = normalizeMessage(
            ticketResult.reason?.data?.message || ticketResult.reason?.message,
            'ไม่สามารถโหลดจำนวนตั๋วได้'
          )
          logPageApiError('Activity', 'getTicketBalance', ticketResult.reason)
          if (isConnectionError(ticketMessage)) {
            setCenterToast(CONNECTION_ERROR_MESSAGE)
          } else {
            setApiPopupTemplate(
              buildApiErrorPopupTemplate({
                title: 'โหลดตั๋วไม่สำเร็จ',
                subtitle: 'Activity',
                message: ticketMessage
              })
            )
          }
        }
        if (meritResult.status === 'fulfilled') {
          const meritMap = normalizeMeritRoomMap(meritResult.value)
          setMeritRoomMap(meritMap)
        } else {
          setMeritRoomMap({})
        }

        if (ownedResult.status === 'rejected' && joinedResult.status === 'rejected') {
          const rawError = ownedResult.reason || joinedResult.reason
          const message = normalizeMessage(rawError?.data?.message || rawError?.message, 'ไม่สามารถโหลดข้อมูลปาร์ตี้ได้')
          if (isConnectionError(message)) {
            setCenterToast(CONNECTION_ERROR_MESSAGE)
          } else {
            setApiPopupTemplate(
              buildApiErrorPopupTemplate({
                title: 'โหลดปาร์ตี้ไม่สำเร็จ',
                subtitle: '',
                message
              })
            )
          }
        }

        const ownedPayload = ownedResult.status === 'fulfilled' ? ownedResult.value : null
        const ownedCode = getGroupCode(ownedPayload)
        const ownedStatus = getGroupStatus(ownedPayload)
        setHasOwnedParty(Boolean(ownedCode) && ownedStatus !== 'expired')
        const joinedList =
          joinedResult.status === 'fulfilled' && Array.isArray(joinedResult.value?.data)
            ? joinedResult.value.data
            : []

        const mapByCode = new Map()
        if (ownedCode) {
          mapByCode.set(ownedCode, {
            ...(ownedPayload?.data || ownedPayload || {}),
            group_code: ownedCode,
            is_owner: true
          })
        }

        joinedList.forEach((group) => {
          const code = getGroupCode(group)
          if (!code) return
          const prev = mapByCode.get(code) || {}
          mapByCode.set(code, {
            ...prev,
            ...group,
            group_code: code,
            is_owner: Boolean(prev?.is_owner || group?.is_owner || code === ownedCode)
          })
        })

        const groups = Array.from(mapByCode.values()).filter((group) => {
          const status = getGroupStatus(group)
          return status !== 'expired'
        })
        const groupsWithRealMembersCount = await Promise.all(
          groups.map(async (group, index) => {
            const code = getGroupCode(group) || `group-${index + 1}`
            const isOwner = Boolean(group?.is_owner || code === ownedCode)
            const fallbackMembersCount =
              countUniqueMembers(group?.members) ||
              toSafeNumber(group?.members_count) ||
              1
            let membersCount = fallbackMembersCount
            let leaderAvatar = group?.owner?.picture_url || localPicture || '🙂'
            if (code && !code.startsWith('group-')) {
              try {
                const membersResponse = await muteluApi.getPartyMembers(code)
                const membersList =
                  Array.isArray(membersResponse?.data) ? membersResponse.data :
                    Array.isArray(membersResponse?.members) ? membersResponse.members :
                      Array.isArray(membersResponse) ? membersResponse :
                        []
                const countedMembers = countUniqueMembers(membersList)
                if (countedMembers > 0) {
                  membersCount = countedMembers
                }
                const ownerMember = membersList.find((member) => member?.is_owner)
                if (ownerMember?.picture_url) {
                  leaderAvatar = ownerMember.picture_url
                }
              } catch (error) {
                logPageApiError('Activity', 'getPartyMembers(count)', error)
              }
            }
            return {
              id: code,
              name: group?.group_name || `ตี้ ${code}`,
              avatar: leaderAvatar,
              role: getPartyRoleLabel(isOwner, getGroupStatus(group)),
              members: `สมาชิก ${membersCount} / 4 คน`,
              status: getGroupStatus(group),
              to: `/game/mutelu/party/${code}`
            }
          })
        )
        const rows = groupsWithRealMembersCount

        if (rows.length > 0) {
          setPartyApiRows(rows)
        } else {
          setPartyApiRows([])
        }
        if (shouldForcePartyRefresh) {
          localStorage.removeItem(FORCE_PARTY_REFRESH_KEY)
        }
      } catch (error) {
        logPageApiError('Activity', 'bootstrap', error)
        if (mounted) setPartyApiRows([])
        if (mounted) setHasOwnedParty(false)
        if (mounted) setTicketBalance(DEFAULT_TICKET_BALANCE)
        if (mounted) setMeritRoomMap({})
        if (mounted) {
          setCenterToast(CONNECTION_ERROR_MESSAGE)
        }
      }
    }

    fetchParties()
    return () => {
      mounted = false
    }
  }, [isMemberMode, localPicture, location.key])

  useEffect(() => {
    if (!centerToast) return undefined
    if (centerToastTimerRef.current) {
      window.clearTimeout(centerToastTimerRef.current)
    }
    centerToastTimerRef.current = window.setTimeout(() => setCenterToast(''), 1700)
    return () => {
      if (centerToastTimerRef.current) {
        window.clearTimeout(centerToastTimerRef.current)
      }
    }
  }, [centerToast])

  return (
    <section className="mobile-page activity-page mx-auto w-full max-w-md">
      <Card className="mobile-panel activity-card">
        <div className="landing-header px-4 py-4 md:px-6">
          <p className="landing-badge">Big C Receipt Mutelu</p>
          <h1 className="landing-title mt-1 md:text-3xl">{state.campaign.campaignName}</h1>
          <p className="landing-subtitle mt-1">ภารกิจสะสมพลังมูแบบ Step ต่อ Step</p>
        </div>

        <div className="activity-ticket-wrap">
          <div className="activity-ticket-grid">
            {ticketBuckets.map((ticket) => (
              <div key={ticket.tone} className={`activity-ticket ${ticket.tone}`}>
                <p className="activity-ticket-label">{ticket.label}</p>
                <p className="activity-ticket-value">
                  <span>{ticket.count}</span>
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="activity-steps">
          {!isMemberMode ? (
            <section className="activity-step step-1">
            <div className="activity-step-head">
              <span className="activity-step-index">Step 1</span>
              <div className="activity-step-title-wrap">
                <h2 className="activity-step-title">สแกน QRcode</h2>
                <hr className="activity-step-hr" />
              </div>
            </div>
            <p className="activity-step-sub">สแกนบิลเพื่ออัปเดตดวงและปลดล็อกกิจกรรมถัดไป</p>
            <Link to="/game/mutelu/scan" className="w-full flex justify-center">
              <Button className="landing-btn landing-btn-scan btn-uniform activity-scan-btn">
                Scan QRcode
                <span className="landing-scan-icon activity-scan-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 4H5a1 1 0 0 0-1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M17 4h2a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M7 20H5a1 1 0 0 1-1-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M17 20h2a1 1 0 0 0 1-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M6.5 12h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </span>
              </Button>
            </Link>
            </section>
          ) : null}

          {!isMemberMode && showPartyAndMeritSteps && partyRows.length > 0 ? (
            <div className="activity-step-divider" aria-hidden="true" />
          ) : null}

          {showPartyAndMeritSteps && displayPartyRows.length > 0 ? (
            <section className="activity-step step-2">
            <div className="activity-step-head">
              <span className="activity-step-index">{isMemberMode ? 'Step 1' : 'Step 2'}</span>
              <div className="activity-step-title-wrap">
                <h2 className="activity-step-title">รายการปาร์ตี้</h2>
                <hr className="activity-step-hr" />
              </div>
            </div>
            <div className="activity-party-list">
              {displayPartyRows.map((party) => (
                <Link
                  key={party.id}
                  to={party.to || `/game/mutelu/party/${party.id}`}
                  state={{ roomId: party.id, roomTitle: party.name }}
                  className="activity-party-link"
                >
                  <div className="activity-party-item">
                    <div className="activity-party-avatar">
                      <UserAvatar
                        src={party.avatar}
                        alt={party.name}
                        fallback="🙂"
                        imageClassName="activity-party-avatar-img"
                      />
                    </div>
                    <div className="activity-party-content">
                      <p className="activity-party-title">{party.name}</p>
                      <p className="activity-party-sub">{party.members}</p>
                    </div>
                    <span className={`activity-party-role ${party.role === 'ลูกตี้' ? 'member' : 'leader'}`}>{party.role}</span>
                  </div>
                </Link>
              ))}
            </div>
            </section>
          ) : null}

          {showPartyAndMeritSteps && bathRooms.length > 0 ? (
            <div className="activity-step-divider" aria-hidden="true" />
          ) : null}

          {showPartyAndMeritSteps && bathRooms.length > 0 ? (
            <section className="activity-step step-3">
            <div className="activity-step-head">
              <span className="activity-step-index">{isMemberMode ? 'Step 2' : 'Step 3'}</span>
              <div className="activity-step-title-wrap">
                <h2 className="activity-step-title">ภารกิจสรงน้ำ</h2>
                <hr className="activity-step-hr" />
              </div>
            </div>
            <p className="activity-step-sub">เข้าไปดูภารกิจสรงน้ำวันนี้</p>
            <div className="activity-bath-list">
              {bathRooms.map((room) => {
                const roomStatus = room.bathDoneToday ? 'สรงน้ำแล้ววันนี้' : 'ยังไม่สรงน้ำวันนี้'
                return (
                  <Link
                    key={`bath-${room.id}`}
                    to={`/game/mutelu/merit-bath/${room.id}`}
                    state={{
                      roomTitle: room.name,
                      roomStatus,
                      membersCount: room.membersCount,
                      filledSegments: room.filledSegments,
                      passToday: room.passToday
                    }}
                    className="activity-bath-link"
                  >
                    <div className="activity-bath-item">
                      <div className="activity-party-avatar">
                        <UserAvatar
                          src={room.ownerAvatar}
                          alt={room.name}
                          fallback="🙂"
                          imageClassName="activity-party-avatar-img"
                        />
                      </div>
                      <div className="activity-bath-content">
                        <p className="activity-bath-title">{room.name}</p>
                        <p className="activity-bath-sub">{roomStatus}</p>
                      </div>
                      {room.bathDoneToday ? (
                        <Button type="button" className="activity-bath-btn is-disabled" disabled>
                          สรงน้ำแล้ว
                        </Button>
                      ) : (
                        <Button type="button" className="activity-bath-btn">ไปสรงน้ำ</Button>
                      )}
                    </div>
                  </Link>
                )
              })}
            </div>
            </section>
          ) : null}

          {showStep4 ? (
            <>
              <div className="activity-step-divider" aria-hidden="true" />
              <section className="activity-step step-4">
                <div className="activity-step-head">
                  <span className="activity-step-index">{isMemberMode ? 'Step 3' : 'Step 4'}</span>
                  <div className="activity-step-title-wrap">
                    <h2 className="activity-step-title">Dashboard</h2>
                    <hr className="activity-step-hr" />
                  </div>
                </div>
                <p className="activity-step-sub">ดูอันดับคะแนนจากตั๋วทอง เงิน ทองแดง ของแต่ละปาร์ตี้</p>
                <Link to="/game/mutelu/dashboard" className="w-full flex justify-center">
                  <Button className="landing-btn btn-uniform activity-dashboard-btn">
                    ดูอันดับคะแนน
                  </Button>
                </Link>
              </section>
            </>
          ) : null}
        </div>
      </Card>

      <CenterPopup
        open={Boolean(apiPopupTemplate)}
        template={apiPopupTemplate}
        onCancel={() => setApiPopupTemplate(null)}
        onConfirm={() => setApiPopupTemplate(null)}
      />
      <CenterToast open={Boolean(centerToast)} message={centerToast} />
    </section>
  )
}
